import React, { createContext, useContext, useState, ReactNode } from 'react'
import {
  ClientResponse,
  TerminalResponse,
  CertificateResponse,
} from '../../types'
import { useCertificateContext } from './CertificateProvider'
import { usePinContext } from './PinProvider'
import { UyapAuthenticator } from '../services/uyap-authenticator'
import { trpc } from '../utils'
import { useSnackbar } from 'notistack'
import axios from 'axios'

interface LoginContextType {
  loginLoading: boolean
  loginResult: any
  login: () => Promise<void>
  sendLoginInfoToExtension: () => Promise<void>
  uyapAuthenticator: UyapAuthenticator
}

const LoginContext = createContext<LoginContextType | undefined>(undefined)

// Remote sign function
const remoteSign = async (
  client: ClientResponse,
  certificate: {
    cert: CertificateResponse
    terminal: TerminalResponse
  },
  dataToSign: string,
  pinCode: string,
) => {
  return await trpc.signData.mutate({
    library: certificate.terminal.library,
    slotId: certificate.terminal.slotId,
    certSerialNumber: certificate.cert.serialNumber,
    dataBase64: btoa(dataToSign),
    pincode: pinCode,
    addSigningTime: true,
    clientId: client.clientId,
  })
}

const BASE_URL = window.location.origin

export const LoginProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { selectedCertificate } = useCertificateContext()
  const { pinCode } = usePinContext()
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginResult, setLoginResult] = useState<any>(null)
  const { enqueueSnackbar } = useSnackbar()

  // UYAP Authenticator
  const uyapAuthenticator = new UyapAuthenticator(
    {
      initializeURL: `${BASE_URL}/web.uyap`,
      finalizeURL: `${BASE_URL}/web.uyap`,
      loginURL: `${BASE_URL}/login.uyap`,
    },
    (params) =>
      remoteSign(
        params.client,
        params.certificate,
        params.dataToSign,
        params.pinCode,
      ),
  )

  const login = async () => {
    if (!selectedCertificate) {
      enqueueSnackbar('Lütfen bir sertifika seçin!', { variant: 'warning' })
      return
    }

    if (!pinCode.trim()) {
      enqueueSnackbar('Lütfen PIN kodunu girin!', { variant: 'warning' })
      return
    }

    setLoginLoading(true)
    try {
      const result = await uyapAuthenticator.login({
        client: selectedCertificate.client,
        terminal: selectedCertificate.terminal,
        certificate: selectedCertificate.cert,
        pinCode,
      })

      await sendLoginInfoToExtension()
      setLoginResult(result)
    } catch (error) {
      console.error('Login failed:', error)
      setLoginResult({ success: false, error: String(error) })
      throw error
    } finally {
      setLoginLoading(false)
    }
  }

  const sendLoginInfoToExtension = async () => {
    await axios.get(
      `${BASE_URL}/api-set-cookie?name=${selectedCertificate?.cert?.subjectCommonName}`,
    )
  }

  const contextValue: LoginContextType = {
    loginLoading,
    loginResult,
    login,
    sendLoginInfoToExtension,
    uyapAuthenticator,
  }

  return (
    <LoginContext.Provider value={contextValue}>
      {children}
    </LoginContext.Provider>
  )
}

export const useLoginContext = () => {
  const context = useContext(LoginContext)
  if (context === undefined) {
    throw new Error('useLoginContext must be used within a LoginProvider')
  }
  return context
}
